import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Header } from './Header';
import { AlignContent } from './helpers/AlignContent';
import { Container } from './helpers/Container';
import { Title } from './Title';
import { Card, CardBody, CardFooter } from './Card';
import { FormField } from './form/FormField';
import { CircularProgress, Fade, FormHelperText, makeStyles, MenuItem, Select } from '@material-ui/core';
import { Input } from './form/Input';
import { Label } from './form/Label';
import { selectUser } from '../store/auth/auth.selector';
import { useSelector } from 'react-redux';
import { Button } from './buttons/Button';
import { Send } from '@material-ui/icons';
import { FormRow } from './form/FormRow';
import { getReceiptFormInfo } from '../store/registration/actions/getReceiptFormInfo';
import { Loading } from './Loading';
import { Error } from './Error';
import { AnimatedContent } from './helpers/AnimatedContent';
import { orderReceipt } from '../store/registration/actions/orderReceipt';
import { RequestState } from '../model/enums/RequestState';
import { Alert } from '@material-ui/lab';
import { Colors } from '../constants/colors';
import { getApiErrorMessage } from '../hooks/getApiErrorMessage';
import { useMountEffect } from '../hooks/useMountEffect';
import { useNavigate } from 'react-router-dom';

const useStyles = makeStyles({
  receiptCard: {
    width: '100%',
    maxWidth: '100% !important',
    margin: '20px 0 0 !important'
  },
  progressCircle: {
    color: Colors.white,
    marginLeft: 10
  }
});

export const ReceiptOrder = () => {
  const classes = useStyles();
  const user = useSelector(selectUser);
  const navigate = useNavigate();

  const [clinics, setClinics] = useState<string[] | undefined>(undefined);
  const [selectedClinic, setSelectedClinic] = useState<string>('');
  const [note, setNote] = useState('');

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [requestState, setRequestState] = useState<RequestState>(RequestState.WAITING);

  /** Init form info **/
  useMountEffect(async () => {
    setLoading(true);
    try {
      const formInfo = await getReceiptFormInfo();
      setClinics(formInfo.clinics);
    } catch (e) {
      setErrorMessage(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  });

  const isReceiptButtonDisabled = (): boolean => {
    return selectedClinic.trim().length === 0;
  };

  const handleReceiptOrder = async () => {
    if (!user?.email) {
      return;
    }

    setRequestState(RequestState.LOADING);
    try {
      await orderReceipt({
        clinic: selectedClinic,
        user,
        note
      });
      setRequestState(RequestState.SUCCESS);
    } catch (e) {
      setErrorMessage(getApiErrorMessage(e));
      setRequestState(RequestState.ERROR);
    } finally {
    }

    navigate('/send-receipt-order');
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <Header showLogout={true} />
      <Container>
        <Title>Žádost o vystavení eReceptu</Title>

        <Loading show={loading} />
        <Error errorMessage={errorMessage}
               onClose={() => setErrorMessage('')} />

        <Fade in={!loading && clinics !== undefined}>
          <AnimatedContent>
            <Card className={classes.receiptCard}>
              <CardBody>
                <FormRow>
                  <FormField>
                    <Label>Vaše telefonní číslo:</Label>
                    <Input disabled={true}
                           value={user.phone}
                           name='phone' />
                  </FormField>

                  <FormField>
                    <Label>Váš e-mail:</Label>
                    <Input disabled={true}
                           value={user.email}
                           type='email'
                           name='email' />
                  </FormField>
                </FormRow>

                {clinics !== undefined && <FormField>
                  <Label>Klinika:</Label>
                  <Select value={selectedClinic}
                          onChange={(e) => setSelectedClinic(e.target.value as string)}
                          input={<Input />}>
                    {clinics.map((clinic, key) =>
                      <MenuItem key={key}
                                value={clinic}>
                        {clinic}
                      </MenuItem>
                    )}
                    <MenuItem value=''>- Nevybráno - </MenuItem>
                  </Select>
                  <FormHelperText>
                    Na této klinice Vám bude vystaven recept.
                  </FormHelperText>
                </FormField>}


                <FormField>
                  <Label>Text žádosti:</Label>
                  <Input multiline={true}
                         rows={4}
                         onChange={e => setNote(e.target.value)}
                         defaultValue={note}
                         name='note' />
                  <FormHelperText>
                    Váš text žádosti k vystavení receptu.
                  </FormHelperText>
                </FormField>
              </CardBody>

              <CardFooter>
                <AlignContent to='center' expand={true}>
                  {(() => {
                    if (requestState === RequestState.LOADING) {
                      return <Button disabled={true}>
                        Provádíme objednání receptu <CircularProgress size={24} className={classes.progressCircle} />
                      </Button>;
                    } else if (requestState === RequestState.SUCCESS) {
                      return <Alert variant='filled'
                                    severity='success'>
                        Recept byl úspěšně objednán.
                      </Alert>;
                    } else {
                      return <Button onClick={handleReceiptOrder}
                                     disabled={isReceiptButtonDisabled()}
                                     type='submit'>
                        Objednat recept <Send />
                      </Button>;
                    }
                  })()}
                </AlignContent>
              </CardFooter>
            </Card>
          </AnimatedContent>
        </Fade>
      </Container>
    </>
  );
};

